import React, { useState, useEffect } from 'react';
import { 
  Wifi, 
  WifiOff, 
  CheckCircle, 
  AlertTriangle, 
  RefreshCw,
  Settings,
  X
} from 'lucide-react';
import { connectionMonitor } from '../services/connectionMonitor';

interface ConnectionStatusIndicatorProps {
  position?: 'header' | 'sidebar';
  showDetails?: boolean;
}

const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({ 
  position = 'header',
  showDetails = false 
}) => {
  const [connectionStatuses, setConnectionStatuses] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [overallHealth, setOverallHealth] = useState({ healthy: true, connectedCount: 0, totalCount: 0 });

  useEffect(() => {
    // Load initial statuses
    updateStatuses();

    // Listen for connection status changes
    const handleConnectionChange = () => {
      updateStatuses();
    };

    window.addEventListener('connection-status-changed', handleConnectionChange);

    return () => {
      window.removeEventListener('connection-status-changed', handleConnectionChange);
    };
  }, []);

  const updateStatuses = () => {
    const statuses = connectionMonitor.getConnectionStatuses();
    const health = connectionMonitor.getOverallHealth();
    
    setConnectionStatuses(statuses);
    setOverallHealth(health);
  };

  const handleForceCheck = async (service: string) => {
    await connectionMonitor.checkService(service);
    updateStatuses();
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'supabase': return '🗄️';
      case 'firebase': return '🔥';
      case 'google_calendar': return '📅';
      case 'google': return '📅';
      case 'openai': return '🤖';
      case 'fathom': return '📊';
      case 'grafana': return '📈';
      case 'wasabi': return '☁️';
      default: return '🔗';
    }
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case 'supabase': return 'Base de Datos';
      case 'firebase': return 'Notificaciones';
      case 'google_calendar': return 'Google Calendar';
      case 'google': return 'Google Calendar';
      case 'openai': return 'OpenAI (IA)';
      case 'fathom': return 'Fathom Analytics';
      case 'grafana': return 'Grafana (Monitoreo)';
      case 'wasabi': return 'Wasabi (Storage)';
      default: return service;
    }
  };

  if (position === 'sidebar') {
    return (
      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {overallHealth.healthy ? (
              <Wifi className="w-4 h-4 text-green-600" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-600" />
            )}
            <span className="text-xs text-gray-600">
              {overallHealth.connectedCount}/{overallHealth.totalCount} servicios
            </span>
          </div>
          {!overallHealth.healthy && (
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center space-x-2 p-2 rounded-lg transition-colors ${
          overallHealth.healthy 
            ? 'text-green-600 hover:bg-green-50' 
            : 'text-red-600 hover:bg-red-50'
        }`}
        title={`${overallHealth.connectedCount}/${overallHealth.totalCount} servicios conectados`}
      >
        {overallHealth.healthy ? (
          <Wifi className="w-5 h-5" />
        ) : (
          <WifiOff className="w-5 h-5" />
        )}
        {showDetails && (
          <span className="text-sm font-medium">
            {overallHealth.connectedCount}/{overallHealth.totalCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Estado de Conexiones</h3>
              <button
                onClick={() => setShowDropdown(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {connectionStatuses.map(status => (
              <div key={status.service} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{getServiceIcon(status.service)}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {getServiceLabel(status.service)}
                    </p>
                    {status.error && (
                      <p className="text-xs text-red-600">{status.error}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Verificado: {status.lastChecked.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {status.isConnected ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  )}
                  <button
                    onClick={() => handleForceCheck(status.service)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    title="Verificar ahora"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">
                Próxima verificación en 5 min
              </span>
              <button
                onClick={() => {
                  setShowDropdown(false);
                  // Navigate to settings
                  console.log('Navigate to settings');
                }}
                className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <Settings className="w-3 h-3" />
                <span>Configurar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatusIndicator;